import data from "./data";
const IDX_TEXT = 0;
const IDX_MIN = 1;
const IDX_MAX = 2;
const IDX_COLOR = 3;
const IDX_GROUP = 4;
var mLevelsTree = null;

import paxios from "@/common/paxios";
import { setupCache } from "axios-cache-adapter";
import { API_SERVICE, OUT_OF_RANGE } from "@/common/consts";
import { trackOverLimitQuery } from "@/common/functions";

const cache = setupCache({
	exclude: { query: false },
	debug: process.env.NODE_ENV !== "production",
});

var wordscapesPaxios = paxios.create({
	baseURL: API_SERVICE,
	adapter: cache.adapter,
});

const getGroupsByText = (text) => {
	return data.groups
		.filter((e) => e[IDX_TEXT].startsWith(text))
		.map((e) => {
			return {
				type: "GROUP",
				name: `${e[IDX_TEXT]}`,
				min: e[IDX_MIN],
				max: e[IDX_MAX],
				id: `${e[IDX_TEXT]}`,
			};
		});
};

const getPacksByText = (text) => {
	return data.packs
		.filter((e) => e[IDX_TEXT].startsWith(text))
		.map((e) => {
			return {
				type: "PACK",
				name: `${e[IDX_TEXT]}`,
				group: `${e[IDX_GROUP]}`,
				min: e[IDX_MIN],
				max: e[IDX_MAX],
				id: `${e[IDX_GROUP]}>${e[IDX_TEXT]}`,
			};
		});
};

export const search = (text, page = "GROUP") => {
	let results = [];
	if (!text) return results;
	try {
		// Handle numeric search
		if (/^(level\s*)?\d+$/i.test(text)) {
			let level = Number(text.replace(/^level\s*/i, ""));
			let l = getLevelSummaryById(level);
			if (l) {
				results.push(l);
				// Add the following 4 results if they exist. e.g. 15 -> 15, [150, 151, 152, 153]
				for (let i = 0; i < 4; i++) {
					l = getLevelSummaryById(level * 10 + i);
					if (!l) break;
					results.push(l);
				}
			}
		} else if (text.length >= 2) {
			// Handle text search
			text = text.toLowerCase(); // Case insensitive search

			const isGroupSearch = /^group\s/i.test(text);
			const isPackSearch = /^pack\s/i.test(text);

			if (isGroupSearch || isPackSearch) {
				const replaceRegex = isGroupSearch ? /^group\s/i : /^pack\s/i;
				text = text.replace(replaceRegex, "").trim();
				if (text.length < 2) return results;
			}

			let groups, packs;

			// If this is not a pack-only search, check the groups. And vice-versa.
			if (!isPackSearch) {
				groups = getGroupsByText(text);
			}

			if (!isGroupSearch) {
				packs = getPacksByText(text);
			}

			// If this is either a group-only or a pack-only search, return the relevant result arrays.
			if (isGroupSearch) return groups;
			if (isPackSearch) return packs;

			results =
				page === "GROUP"
					? [].concat(groups).concat(packs)
					: [].concat(packs).concat(groups);
		}
	} catch (err) {
		console.error(err);
	}

	return results;
};

function getLevelSummaryById(level) {
	try {
		let e = data.packs.find(
			(e) => e[IDX_MIN] <= level && level <= e[IDX_MAX]
		);
		return e
			? {
					type: "LEVEL",
					name: `${level}`,
					group: `${e[IDX_GROUP]}`,
					pack: `${e[IDX_TEXT]}`,
					min: level,
					max: level,
					id: `${e[IDX_GROUP]}>${e[IDX_TEXT]}>${level}`,
			  }
			: null;
	} catch (err) {
		console.error(err);
		return null;
	}
}

export const listGroups = () => {
	let groups;
	try {
		groups = data.groups.map((e) => {
			return getGroupInfo(e[IDX_TEXT]);
		});
	} catch (err) {
		console.error(err);
	}
	return groups;
};

export const getGroupInfo = (groupId) => {
	let groupInfo;
	try {
		if (!mLevelsTree) buildLevelsTree();

		let g = mLevelsTree[groupId];
		groupInfo = { ...g };
	} catch (err) {
		console.error(err);
	}
	return groupInfo;
};

export const getPackInfo = (groupId, packId) => {
	let packInfo;
	if (!groupId || !packId) return packInfo;
	try {
		if (!mLevelsTree) buildLevelsTree();

		let p = mLevelsTree[groupId]
			? mLevelsTree[groupId].packs[packId]
			: undefined;
		if (typeof p === "undefined") return packInfo;
		packInfo = { ...p };
		packInfo.levels = [];
		for (let i = p.min; i <= p.max; i++) {
			packInfo.levels.push({
				name: `${p.name} ${i - p.min + 1}`,
				level: i,
			});
		}
	} catch (err) {
		console.error(err);
	}
	return packInfo;
};

export const getLevelInfo = async (levelId, store) => {
	return wordscapesPaxios
		.request({
			url: `v3/wordscapes?level=${levelId}`,
			method: "get",
		})
		.then(
			(response) => {
				if (response.data.message === OUT_OF_RANGE) {
					trackOverLimitQuery(store, levelId);
				}
				return response.data.data._items;
			},
			(error) => {
				console.log(
					`Error retrieving the level solution details: `,
					error
				);
				return null;
			}
		);
};

/**
 * Retrieves the daily puzzle solution details.
 * Parameters:
 * 	year: 2019,2020,... (YYYY)
 * 	month: 1..12 (M)
 *  day: 1..31 (D)
 */
export const getDailyPuzzleInfo = async (year, month, day) => {
	return wordscapesPaxios
		.request({
			url: `v3/wordscapes_daily?level=${year}-${month}-${day}`,
			method: "get",
		})
		.then(
			(response) => {
				return response.data.data._items;
			},
			(error) => {
				console.log(
					`Error retrieving the daily solution details: `,
					error
				);
				return null;
			}
		);
};

function buildLevelsTree() {
	mLevelsTree = {};
	data.groups.forEach((g) => {
		mLevelsTree[g[IDX_TEXT]] = {
			name: g[IDX_TEXT],
			min: g[IDX_MIN],
			max: g[IDX_MAX],
			color: g[IDX_COLOR],
			// packs: List of packs within this group
		};
	});
	data.packs.forEach((p) => {
		try {
			if (!mLevelsTree[p[IDX_GROUP]].packs) {
				mLevelsTree[p[IDX_GROUP]].packs = {};
				mLevelsTree[p[IDX_GROUP]].packsList = [];
			}
			let packInfo = {
				name: p[IDX_TEXT],
				min: p[IDX_MIN],
				max: p[IDX_MAX],
				color: p[IDX_COLOR],
				group: p[IDX_GROUP],
			};
			mLevelsTree[p[IDX_GROUP]].packs[packInfo.name] = packInfo;
			mLevelsTree[p[IDX_GROUP]].packsList.push(packInfo);
		} catch (err) {
			console.error(`Error processing`, p, err);
		}
	});
}
